import { AmountDisplay } from '@/design-system/amount-display/amount-display';
import { Badge } from '@/design-system/badge/badge';
import { Inline } from '@/design-system/inline/inline';
import { TransactionView } from '@/types/entity-types';
import { cn } from '@/utils/cn';
import { UTCDate } from '@/utils/dates';
import { getRelativeDateText } from '@/utils/dates/get-relative-date-text';
import { exhaustiveCheck } from '@/utils/exhaustive-check';
import { getNormalizedAmount } from '@/utils/get-normalized-amount';
import { sortByDate } from '@/utils/tables/sort-by-date';
import { tw } from '@/utils/tw';
import { TransactionStatus } from '@seapoint/server/enums';
import { createColumnHelper } from '@tanstack/react-table';
import { ArrowDown, ArrowUpRight } from 'lucide-react';

export const TRANSACTION_DATE_COLUMN_ID = 'transaction_activity_date';

const columnHelper = createColumnHelper<TransactionView>();

export const treasuryAccountTransactionsColumnDefinition = [
  columnHelper.accessor((row) => new UTCDate(row.createdAt), {
    id: TRANSACTION_DATE_COLUMN_ID,
    header: 'Date',
    cell: ({ row }) => {
      const createdAtDate = new UTCDate(row.original.createdAt);

      return <p className="text-sm text-black">{getRelativeDateText(createdAtDate)}</p>;
    },
    sortingFn: sortByDate,
    size: 12,
    minSize: 12,
  }),
  columnHelper.accessor((row) => row.description, {
    id: 'description',
    header: 'Description',
    cell: ({ row }) => {
      const icon = getDescriptionIcon(row.original.isCredit);
      const isTransactionPending = row.original.status === TransactionStatus.PENDING;

      return (
        <Inline space={2} wrap="nowrap">
          {icon}

          <p className="truncate text-sm font-normal text-foreground">{row.original.description}</p>

          {isTransactionPending && <Badge>Pending</Badge>}
        </Inline>
      );
    },
    size: 40,
  }),
  columnHelper.accessor(
    (row) => {
      return getNormalizedAmount({ amount: row.amount, isCredit: row.isCredit });
    },
    {
      id: 'amount',
      header: () => <p className="text-right">Amount</p>,
      cell: ({ row }) => {
        const normalizedAmount = getNormalizedAmount({
          amount: row.original.amount,
          isCredit: row.original.isCredit,
        });
        const isPending = row.original.status === TransactionStatus.PENDING;

        return (
          <AmountDisplay
            normalizedAmount={normalizedAmount}
            currency={row.original.currency}
            className={cn('text-right text-sm font-normal text-foreground', {
              'text-muted-foreground': isPending,
              'text-success': row.original.isCredit,
            })}
          />
        );
      },
      size: 15,
      minSize: 15,
    },
  ),
];

const getDescriptionIcon = (isCredit: boolean) => {
  const iconClassName = tw`h-4 w-4 text-foreground`;

  switch (isCredit) {
    case true:
      return <ArrowDown className={iconClassName} />;
    case false:
      return <ArrowUpRight className={iconClassName} />;
    default:
      return exhaustiveCheck(isCredit);
  }
};
